import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Grid,
} from '@material-ui/core'
import { EnotLinks, MainPageContainer, ModelList, PaginatedResults, Sidebar } from '../Organisms'
import { SearchService } from '../../services'
import { useParams } from '../../utils/Hooks'
import { useAsyncError } from '../../utils/Error'
import { UrlManipulation } from '../../utils'
import { clauseClickEvent } from '../../utils/Analytics'

const EMPTY_CLAUSE_RESULT = { clauses: [], hasNext: false, amount: 0 }

const clickCardCallback = (clause, setSidebarClause, i18n) => {
  setSidebarClause(clause)
  clauseClickEvent(clause.clause_id)
}

const SearchResults = () => {
  const { i18n, t } = useTranslation()
  const throwError = useAsyncError()
  const params = useParams()
  const [sidebarClause, setSidebarClause] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [clauseSearchResult, setClauseSearchResult] = React.useState(null)
  const [modelSearchResult, setModelSearchResult] = React.useState(null)

  const handleClickCard = React.useCallback(clause => clickCardCallback(clause, setSidebarClause, i18n), [i18n])

  const [variant, setVariant] = React.useState(null)

  React.useEffect(() => {
    setVariant(
      params.subsection === 'models'
        ? 'models'
        : 'clauses'
    )
  }, [params.subsection])

  React.useEffect(() => {
    setLoading(true)
    let isSubscribed = true
    let modelsFinished = false
    let clausesFinished = false

    if (variant === 'models') {
      setClauseSearchResult(null)
      SearchService.searchModels(params, params.page, params.page_size)
        .then(result => {
          if (isSubscribed) {
            setModelSearchResult(result)
            setLoading(false)
          }
        })
        .catch(error => {
          throwError(error)
          setLoading(false)
        })
    } else if (variant === 'clauses') {
      SearchService.searchClauses(params, params.page, params.page_size)
        .then(result => {
          if (isSubscribed) {
            setClauseSearchResult(result)
            clausesFinished = true
            if (modelsFinished) setLoading(false)
          }
        })
        .catch(error => {
          if (error.status === 404) {
            setClauseSearchResult(EMPTY_CLAUSE_RESULT)
            clausesFinished = true
            if (modelsFinished) setLoading(false)
          } else {
            throwError(error)
            if (modelsFinished) setLoading(false)
          }
        })
      SearchService.searchModels(params, 1, 1)
        .then(result => {
          if (isSubscribed) {
            setModelSearchResult(result)
            modelsFinished = true
            if (clausesFinished) setLoading(false)
          }
        })
        .catch(error => {
          throwError(error)
          setLoading(false)
        })
    }
    return () => isSubscribed = false
  }, [i18n.language, throwError, params, variant])

  return (
    <MainPageContainer showFilters>
      <Grid container spacing={5} item xs={11}>
        <Grid item xs={8}>
          {variant === 'clauses' && !loading && params.value && params.value.length > 0 &&
            <React.Fragment>
              {modelSearchResult.amount > 0
                && <Button variant="outlined" color="primary" style={{ backgroundColor: "rgb(0, 118, 145,0.05)", color: '#007691', fontWeight: 'bold', textTransform: 'none' }}
                  onClick={() => UrlManipulation.goToSearch(
                    { subsection: 'models', page: 1 }
                  )}
                >
                  {t('search.modelResults', { amount: modelSearchResult.amount })}
                </Button>
              }
              <ModelList searchResult={modelSearchResult} handleClickCard={handleClickCard} />
            </React.Fragment>
          }
          <PaginatedResults
            handleClickCard={handleClickCard}
            loading={loading || (variant === 'clauses' && !clauseSearchResult)}
            searchResult={variant === 'clauses' ? clauseSearchResult : modelSearchResult}
            variant={variant}
          />
        </Grid>
        <Grid item xs={3}>
          <EnotLinks searchResult={modelSearchResult} showDefaultLink={params.value && params.value.length === 0} loading={loading} />
        </Grid>
      </Grid>
      <Sidebar
        open={sidebarClause}
        clause={sidebarClause}
        onClose={() => setSidebarClause(null)}
      />
    </MainPageContainer>
  )
}

export default SearchResults
